import { IRenter } from "../../types/renter.type";
import { IVehicle } from "../../types/vehicle.type";
import { ApiResponseType, makeRequest } from "../helpers/api.helpers";
import { createDebounce } from "../helpers/debounce.helper";
import { getCompanyId } from "./company.api";

const API_URL = import.meta.env.VITE_API_URL;
const makeRequestDebounced = createDebounce(makeRequest, 200);

export async function getRenters({
  status = [],
  search = "",
  startDate = "",
  endDate = "",
  startDateAtualizadoEm = "",
  endDateAtualizadoEm = "",
  page = 1,
  itemsPerPage = "",
  orderBy = "-updatedAt",
  technicalConsultant = "",
  situation = "",
  fields = "name, cpf, email, cnh, pendencies, status, updatedAt, createdAt, leadOrigin, situation, technicalConsultant, payment, activationData.plan.label, desactivationData.vehicleHistory, companyId, isBlacklisted",
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    fields,
  });
  let statusParam = "";
  if (Array.isArray(status) && status.length > 0) {
    statusParam = status.join(",");
  } else if (typeof status === "string") {
    statusParam = status;
  }
  status && params.append("filters[status]", statusParam);
  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);
  startDate && params.append("filters[startDate]", startDate.toString());
  endDate && params.append("filters[endDate]", endDate.toString());
  startDateAtualizadoEm &&
    params.append(
      "filters[startDateAtualizadoEm]",
      startDateAtualizadoEm.toString()
    );
  endDateAtualizadoEm &&
    params.append(
      "filters[endDateAtualizadoEm]",
      endDateAtualizadoEm.toString()
    );
  technicalConsultant &&
    params.append("filters[technicalConsultant]", technicalConsultant);
  situation && params.append("filters[situation]", situation);

  const url = `${API_URL}/renters?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IRenter[]
  > & { totalItems: number };
  return { ok: result.ok, data: result.data, totalItems: result.totalItems };
}

type PlanoCount = {
  plano: string;
  total: number;
};

export async function getRentersPlansCount() {
  const url = `${API_URL}/renters-plans-count`;
  const result = (await makeRequestDebounced(url)) as Promise<
    ApiResponseType<PlanoCount[]>
  >;
  return result;
}

export async function getRenterCollectors() {
  const url = `${API_URL}/renters/collectors/quantity`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IRenter[]
  >;

  return result;
}

export async function getRenterById(renterId: string) {
  const url = `${API_URL}/renters/${renterId}`;

  const result = (await makeRequestDebounced(url)) as Promise<
    ApiResponseType<IRenter>
  >;

  return result;
}

export type VehiclesSituationType = {
  _id: string;
  name: string;
  situation: string;
  situationDateElapsedDays: number;
  vehicle: IVehicle;
  vehicleSituation: string;
};

export async function getVehiclesSituation() {
  const url = `${API_URL}/renters/vehicle-situation`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    VehiclesSituationType[]
  > & { totalItems: number };

  return { ok: result.ok, data: result.data, totalItems: result.totalItems };
}

export async function getRenterByCpf(cpf: string) {
  const url = `${API_URL}/renters/cpf/${cpf}`;
  return (await makeRequestDebounced(url)) as ApiResponseType<IRenter>;
}

export async function getRentersCount(partnerId: string) {
  const params = new URLSearchParams({
    partnerId: partnerId,
  });
  const url = `${API_URL}/renters/count?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<IRenter>;

  return { ok: result.ok, data: result.data };
}

export async function createRenter(renterData) {
  const data = {
    ...renterData,
    companyId: getCompanyId(),
  };
  const url = `${API_URL}/renters`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IRenter>
  >;
}

const removeReadonlyFields = (updatedData) => {
  delete updatedData._id;
  delete updatedData.__v;
  delete updatedData.contacts;
  delete updatedData.pendencies;
  delete updatedData.status;
  delete updatedData.updatedAt;
  delete updatedData.createdAt;
};

export async function updateRenter(id: string, updatedData) {
  removeReadonlyFields(updatedData);

  const url = `${API_URL}/renters/${id}`;
  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updatedData),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IRenter>
  >;

  return result;
}

export async function updateRenterMotoclub(id: string, updatedData) {
  removeReadonlyFields(updatedData);

  const url = `${API_URL}/renters/motoclub/${id}`;
  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updatedData),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IRenter>
  >;

  return result;
}

export async function updateRenterProps(
  id: string,
  updatedData: Partial<IRenter>
) {
  const url = `${API_URL}/renters/${id}`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updatedData),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IRenter>
  >;

  return result;
}

export async function updateRenterCommercialActions(
  id: string,
  updatedData: Partial<IRenter>
) {
  const url = `${API_URL}/renters/commercial/${id}`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updatedData),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IRenter>
  >;

  return result;
}

type ApproveRenterProps = {
  renterId: string;
  vehicleId: string;
  creditDeposit: number;
  valueDepositCredit?: number;
  period: {
    value: string;
    label: string;
    months: number;
  };
  additions: never[];
  deposit: number;
  subscriptionInitialDate: Date;
  installmentOption: string;
};

export async function approveRenter(data: Partial<ApproveRenterProps>) {
  const url = `${API_URL}/renters/approve`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<unknown>
  >;
  return result;
}

type UnlinkVehicleRenterProps = {
  renterId: string;
  vehicleId: string;
};

export async function unlinkVehicleRenter(data: UnlinkVehicleRenterProps) {
  const url = `${API_URL}/renters/unlink-vehicle`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...data, companyId: getCompanyId() }),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<unknown>
  >;

  return result;
}

type BondRenterVehicleProps = {
  renterId: string;
  vehicleId: string;
};

export async function BondRenterVehicle(data: BondRenterVehicleProps) {
  const url = `${API_URL}/renters/bond-vehicle`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<unknown>
  >;

  return result;
}

export async function convertRenterToLead({ renterId }) {
  const url = `${API_URL}/renters/${renterId}/to-leads`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ renterId }),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IRenter>
  >;
}

type CancelSubscriptionData = {
  dditionalComments?: string;
  amountValue?: number;
  billingPeriod?: string;
  caucao?: string;
  customerId?: string;
  dailyValue?: number;
  finalFineValue?: number;
  fineDetran?: Array<{
    value?: string;
    description?: string;
  }>;
  finePlanValue?: number;
  installmentOptions?: string;
  isCheckedRetraction?: boolean;
  isPostWeekChecked?: boolean;
  paymentDescription?: string;
  paymentMethod?: string;
  reason?: string;
  renterId?: string;
  sinister?: number;
  subscriptionInitialDate?: string | Date;
  valueBlockCommand?: boolean;
  vehicleId?: string;
  newStatusVehicle?: string;
  installmentValue?: string;
  valueBilling: number;
};

export async function cancelSubscription(data: CancelSubscriptionData) {
  const url = `${API_URL}/renters/cancel-subscription`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data }),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IRenter>
  >;
}

export async function finishedSubscription(data: CancelSubscriptionData) {
  const url = `${API_URL}/renters/finished-subscription`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data }),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IRenter>
  >;
}

export async function unfreezePlanRenter(data) {
  const url = `${API_URL}/renters/unfreeze-plan`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IRenter>
  >;
}

type changePlanData = {
  renterId: string;
  vehicleId: string;
  reason: string;
  returnReason: string;
  period?: {
    value: string;
    label: string;
    months: number;
  };
  additions: never[];
  technicalConsultant?: string;
  periodRenter?: string;
  subscriptionInitialDate?: Date;
};

export async function changePlanRenter(updatedData: changePlanData) {
  const url = `${API_URL}/renters/${updatedData.renterId}/change-plan`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updatedData),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<changePlanData>
  >;

  return result;
}

export async function additionsPlanRenter(updatedData: changePlanData) {
  const url = `${API_URL}/renters/${updatedData.renterId}/additions`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updatedData),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<changePlanData>
  >;

  return result;
}

type renewPlanData = {
  renterId: string;
  vehicleId: string;
  newEndDate: Date;
  subscriptionInitialDate: Date;
};

export async function renewPlanRenter(updatedData: renewPlanData) {
  const url = `${API_URL}/renters/${updatedData.renterId}/renewPlan`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updatedData),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<renewPlanData>
  >;

  return result;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function reactivatePlan(data: any, renterId: string) {
  const url = `${API_URL}/renters/${renterId}/reactivate-plan`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<unknown>
  >;

  return result;
}

export async function rentersFindByLincesePlate(
  licensePlate: string | undefined
): Promise<ApiResponseType<IRenter>> {
  const url = `${API_URL}/renters/lincensePlate/${licensePlate}`;

  return (await makeRequestDebounced(url)) as ApiResponseType<IRenter>;
}

export async function startRenterConversation(renterId: string) {
  const url = `${API_URL}/renters/${renterId}/start-conversation`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  };
  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<string>
  >;

  return result;
}
